export const MENU_ITEMS: { name: string; id: string }[] = [
  {
    name: 'Colours',
    id: 'colours',
  },
  {
    name: 'Tokens',
    id: 'tokens',
  },
  {
    name: 'Headings',
    id: 'headings',
  },
  {
    name: 'Body',
    id: 'body',
  },
  {
    name: 'Labels',
    id: 'labels',
  },
  {
    name: 'Spacing',
    id: 'spacing',
  },
  {
    name: 'Icons',
    id: 'icons',
  },
  {
    name: 'Buttons',
    id: 'buttons',
  },
  {
    name: 'Tags',
    id: 'tags',
  },
];

export type Color = {
  name: string;
  hex: string;
  lightColor?: boolean;
};

export const BASE_COLORS: Color[] = [
  {
    name: '$color-brand-red',
    hex: '#ee3524',
  },
  {
    name: '$color-brand-slate',
    hex: '#4e565b',
  },
  {
    name: '$color-brand-blue',
    hex: '#2e6ca5',
  },
  {
    name: '$color-brand-white',
    hex: '#fff',
    lightColor: true,
  },
];

export const NAVY_COLORS: Color[] = [
  {
    name: '$color-navy-100',
    hex: '#060615',
  },
  {
    name: '$color-navy-80',
    hex: '#1f1f2c',
  },
  {
    name: '$color-navy-75',
    hex: '#383844',
  },
  {
    name: '$color-navy-50',
    hex: '#51515b',
  },
  {
    name: '$color-navy-40',
    hex: '#58595b',
  },
  {
    name: '$color-navy-30',
    hex: '#9b9ba1',
  },
];

export const GREYSCALE_COLORS: Color[] = [
  {
    name: '$color-black',
    hex: '#121212',
  },
  {
    name: '$color-grey-100',
    hex: '#262828',
  },
  {
    name: '$color-grey-90',
    hex: '#515353',
  },
  {
    name: '$color-grey-80',
    hex: '#7d7e7e',
  },
  {
    name: '$color-grey-50',
    hex: '#99999f',
  },
  {
    name: '$color-grey-35',
    hex: '#d6d6d6',
    lightColor: true,
  },
  {
    name: '$color-grey-25',
    hex: '#e9eaea',
    lightColor: true,
  },
  {
    name: '$color-grey-15',
    hex: '#f4f4f4',
    lightColor: true,
  },
];

export const COLOR_TOKENS: { name: string; tokens: string[] }[] = [
  {
    name: 'Background',
    tokens: [
      '$background-dark',
      '$background-dark-secondary',
      '$background-light',
      '$background-light-secondary',
      '$background-featured',
      '$background-tag',
      '$background-grey-light',
    ],
  },
  {
    name: 'Copy',
    tokens: [
      '$copy-titles',
      '$copy-copy',
      '$copy-secondary',
      '$copy-dark-primary',
      '$copy-dark-secondary',
      '$copy-red',
      '$copy-disabled',
    ],
  },
  {
    name: 'Buttons',
    tokens: [
      '$buttons-primary',
      '$buttons-primary-text',
      '$buttons-secondary',
      '$buttons-secondary-text',
      '$buttons-tertiary-underline',
      '$buttons-tertiary-text',
      '$buttons-tertiary-underline-inverse',
      '$buttons-tertiary-text-inverse',
      '$buttons-language',
      '$buttons-disabled',
      '$buttons-focus',
    ],
  },
  {
    name: 'Icons',
    tokens: [
      '$icons-on-dark',
      '$icons-on-dark-inactive',
      '$icons-on-light',
      '$icons-red',
    ],
  },
  {
    name: 'Dividers',
    tokens: ['$dividers-on-light', '$dividers-on-dark'],
  },
  {
    name: 'Forms',
    tokens: [
      '$forms-default',
      '$forms-selected',
      '$forms-error',
      '$forms-confirmation',
      '$forms-disabled',
    ],
  },
];

type Heading = {
  name: string;
  placeholder: string;
};

export const HEADINGS: Heading[] = [
  {
    name: 'H1 - Headline 90',
    placeholder: '@extend %h1;',
  },
  {
    name: 'H2 - Headline 68',
    placeholder: '@extend %h2;',
  },
  {
    name: 'H3 - Headline 50',
    placeholder: '@extend %h3;',
  },
  {
    name: 'H4 - Headline 38',
    placeholder: '@extend %h4;',
  },
  {
    name: 'H5 - Headline 32',
    placeholder: '@extend %h5;',
  },
  {
    name: 'H6 - Headline 28',
    placeholder: '@extend %h6;',
  },
  {
    name: 'Headline 22',
    placeholder: '@extend %headline;',
  },
];

type TextEntity = {
  name: string;
  placeholder: string;
  modifier: string;
};

export const BODY_COPY: TextEntity[] = [
  {
    name: 'Body 20 bold',
    placeholder: '@extend %body-20-bold;',
    modifier: '20-bold',
  },
  {
    name: 'Body 20',
    placeholder: '@extend %body-20;',
    modifier: '20',
  },
  {
    name: 'Body 18 bold',
    placeholder: '@extend %body-18-bold;',
    modifier: '18-bold',
  },
  {
    name: 'Body 18',
    placeholder: '@extend %body-18;',
    modifier: '18',
  },
  {
    name: 'Body 16 bold',
    placeholder: '@extend %body-16-bold;',
    modifier: '16-bold',
  },

  {
    name: 'Body 16',
    placeholder: '@extend %body-16;',
    modifier: '16',
  },
  {
    name: 'Body 14 bold',
    placeholder: '@extend %body-14-bold;',
    modifier: '14-bold',
  },
  {
    name: 'Body 14',
    placeholder: '@extend %body-14;',
    modifier: '14',
  },
  {
    name: 'Body 12',
    placeholder: '@extend %body-12;',
    modifier: '12',
  },
];

export const LABELS: TextEntity[] = [
  {
    name: 'Label 14',
    placeholder: '@extend %label-14;',
    modifier: '14',
  },
  {
    name: 'Label 12',
    placeholder: '@extend %label-12;',
    modifier: '12',
  },
];

export const SPACING: { name: string; placeholders: string[] }[] = [
  {
    name: 'Zero - 0px',
    placeholders: ['%spacing-t-zero', '%spacing-b-zero', '%spacing-zero'],
  },
  {
    name: 'Small - 32/40px',
    placeholders: ['%spacing-t-sm', '%spacing-b-sm', '%spacing-sm'],
  },
  {
    name: 'Medium - 56/72px',
    placeholders: ['%spacing-t-md', '%spacing-b-md', '%spacing-md'],
  },
  {
    name: 'Large - 80/112px',
    placeholders: ['%spacing-t-lg', '%spacing-b-lg', '%spacing-lg'],
  },
];

export const ICONS = [
  'anchor-link',
  'arrow-down',
  'arrow-left',
  'arrow-right',
  'arrow-up',
  'chevron-down',
  'chevron-up',
  'clock',
  'cross',
  'filter',
  'language',
  'location',
  'menu',
  'minus',
  'pin',
  'play',
  'plus',
  'search',
  'tick',
];

export type ButtonEntity = {
  name: string;
  textSwipe?: boolean;
  icon?: string;
  noText?: boolean;
};

export const BUTTONS: ButtonEntity[] = [
  { name: 'primary', textSwipe: true, icon: 'arrow-right' },
  { name: 'secondary', textSwipe: true, icon: 'arrow-right' },
  { name: 'tertiary', icon: 'arrow-right' },
  { name: 'anchor' },
  { name: 'link' },
  { name: 'exit', textSwipe: true, icon: 'cross', noText: true },
];

export const DARK_BUTTONS: ButtonEntity[] = [
  { name: 'secondary-dark', textSwipe: true, icon: 'arrow-right' },
  { name: 'tertiary-dark', icon: 'arrow-right' },
  { name: 'large-dark', textSwipe: true, icon: 'arrow-right' },
  { name: 'icon-dark', textSwipe: true, icon: 'play' },
];

export const TAGS: string[] = ['primary', 'secondary', 'tertiary', 'link'];

export const PILLS: string[] = ['pill', 'pill-dark'];
